import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { CheckCircle, AlertCircle, Clock, User, ArrowRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

interface ActionItem {
  id: string
  title: string
  type: 'analysis_ready' | 'review_required' | 'deadline_approaching' | 'finding_critical'
  priority: 'low' | 'medium' | 'high' | 'critical'
  case_id?: string
  case_title?: string
  assigned_to?: string
  due_date?: string
  created_at: string
}

const actionItems: ActionItem[] = []

export function ActionItemsCard() {
  const navigate = useNavigate()
  const { t } = useTranslation('common')

  const typeConfig = {
    analysis_ready: {
      label: t('actions.type.analysisReady'),
      icon: CheckCircle,
      color: 'bg-success/10 text-success border-success/20'
    },
    review_required: {
      label: t('actions.type.reviewRequired'),
      icon: AlertCircle,
      color: 'bg-warning/10 text-warning border-warning/20'
    },
    deadline_approaching: {
      label: t('actions.type.deadlineApproaching'),
      icon: Clock,
      color: 'bg-warning/10 text-warning border-warning/20'
    },
    finding_critical: {
      label: t('actions.type.findingCritical'),
      icon: AlertCircle,
      color: 'bg-destructive/10 text-destructive border-destructive/20'
    }
  }

  const priorityColors = {
    low: 'bg-muted text-muted-foreground border-muted',
    medium: 'bg-warning/10 text-warning border-warning/20',
    high: 'bg-destructive/10 text-destructive border-destructive/20',
    critical: 'bg-destructive text-destructive-foreground border-destructive'
  }

  const handleActionClick = (item: ActionItem) => {
    if (item.case_id) {
      navigate(`/cases/${item.case_id}`)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 1) return t('actions.time.now')
    if (diffInHours < 24) return t('actions.time.hoursAgo', { hours: diffInHours })
    const diffInDays = Math.floor(diffInHours / 24)
    return t('actions.time.daysAgo', { days: diffInDays })
  }

  const formatDueDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 24) return t('actions.time.hoursLeft', { hours: diffInHours })
    const diffInDays = Math.floor(diffInHours / 24)
    return t('actions.time.daysLeft', { days: diffInDays })
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            {t('actions.title')}
          </CardTitle>
          <Badge variant="outline">{actionItems.length}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {actionItems.length === 0 ? (
          <div className="text-center py-8">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">{t('actions.empty.title')}</h3>
            <p className="text-muted-foreground mb-4">{t('actions.empty.description')}</p>
            <Button 
              variant="outline" 
              onClick={() => navigate('/cases')}
            >
              {t('actions.showAllCases')}
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {actionItems.map((item) => {
                const config = typeConfig[item.type]
                const Icon = config.icon
                
                return (
                  <div 
                    key={item.id} 
                    className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => handleActionClick(item)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-start gap-3 flex-1">
                        <Icon className="h-4 w-4 mt-0.5 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm leading-tight">{item.title}</h4>
                          {item.case_title && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {item.case_title}
                            </p>
                          )}
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground ml-2 flex-shrink-0" />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className={config.color} variant="outline">
                          {config.label}
                        </Badge>
                        <Badge className={priorityColors[item.priority]}>
                          {item.priority}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {item.due_date ? formatDueDate(item.due_date) : formatDate(item.created_at)}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            
            <div className="mt-4 pt-3 border-t">
              <Button 
                variant="outline" 
                className="w-full text-sm"
                onClick={() => navigate('/cases')}
              >
                {t('actions.showAllActions')}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}